import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useCentralChatRuntime } from "@/components/CentralChat/centralChatRuntime";
import type {
  CentralChatContext,
  CentralChatIssueAttachment,
} from "@/components/CentralChat/types";

export type CentralChatBridge = {
  openBubble: () => void;
  startNew: () => void;
  setIssueContext: (attachment: CentralChatIssueAttachment) => void;
  clearIssueContext: () => void;
  openIssueChat: (attachment: CentralChatIssueAttachment, draft: string) => void;
  submit: (text: string, contextOverride?: Partial<CentralChatContext>) => void;
};

let bridge: CentralChatBridge | null = null;
const listeners = new Set<() => void>();

function setBridge(next: CentralChatBridge | null) {
  bridge = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getBridge() {
  return bridge;
}

export function useOptionalCentralChatBridge(): CentralChatBridge | null {
  return useSyncExternalStore(subscribe, getBridge, () => null);
}

export function CentralChatBridgeRegister({ children }: { children: ReactNode }) {
  const runtime = useCentralChatRuntime();
  const {
    openBubble,
    startNew,
    setIssueContext,
    clearIssueContext,
    openIssueChat,
    submit,
  } = runtime;
  useEffect(() => {
    setBridge({
      openBubble,
      startNew,
      setIssueContext,
      clearIssueContext,
      openIssueChat,
      submit,
    });
    return () => setBridge(null);
  }, [
    openBubble,
    startNew,
    setIssueContext,
    clearIssueContext,
    openIssueChat,
    submit,
  ]);
  return children;
}
